import {
  ActionMapping,
  GamepadBinding,
  KeyboardBinding,
} from '../types/controls.types';
import {
  KEY_LABELS,
  DungeonAction,
  GameplayContext,
  InterfaceAction,
} from '../data/controls.config';
import { useControlsStore } from '../store/controlsSlice';
import { useInputSourceStore, InputSource } from '../store/inputSourceSlice';
import {
  resolveDisplayChar,
  resolveDisplayCode,
} from '../services/KeyboardLayoutService';

/**
 * Falls back to the raw code when no label is known, so new codes stay
 * readable without crashing.
 */
export function getKeyLabel(code: string): string {
  const display = resolveDisplayCode(code);
  const manual = KEY_LABELS[display];
  if (manual) {
    return manual;
  }
  const ch = resolveDisplayChar(code);
  if (ch) {
    return ch.toUpperCase();
  }
  return KEY_LABELS[code] ?? code;
}

export function getKeyboardLabel(binding: KeyboardBinding): string {
  const main = getKeyLabel(binding.code);
  if (!binding.modifiers || binding.modifiers.length === 0) {
    return main;
  }
  const mods = binding.modifiers.map(getKeyLabel);
  return [...mods, main].join('+');
}

export function getGamepadLabel(binding: GamepadBinding): string {
  switch (binding.type) {
    case 'button':
      return binding.label;
    case 'axis':
      return binding.label;
    case 'combo':
      return `${binding.modifierLabel}+${binding.buttonLabel}`;
  }
}

export function getActionLabel(
  mapping: ActionMapping,
  source: InputSource,
): string {
  return source === 'gamepad'
    ? getGamepadLabel(mapping.gamepad)
    : getKeyboardLabel(mapping.keyboard);
}

export type ActionForContext<C extends GameplayContext> = C extends 'dungeon'
  ? DungeonAction
  : InterfaceAction;

/**
 * Returns `''` when the action is unknown (missing mapping), so the caller
 * can cleanly do `if (!label) return null;`.
 */
export function useActionLabel<C extends GameplayContext>(
  context: C,
  action: ActionForContext<C>,
): string {
  const lastSource = useInputSourceStore((s) => s.lastSource);
  const mapping = useControlsStore((s) => {
    const ctxMap = context === 'interface' ? s.interface : s.dungeon;
    return (ctxMap as Record<string, ActionMapping>)[action as string];
  });
  if (!mapping) {
    return '';
  }
  return getActionLabel(mapping, lastSource);
}
